import {
  CategoryDebt,
  CHAPTER_2,
  CHAPTER_3,
  TopicEducationBenefitOverpayments,
  TopicEducationBenefitOverpaymentsForStudents,
  yourRoleOptionsEducation,
} from '../../constants';
import {
  isBranchOfServiceRequired,
  isHealthFacilityRequired,
  isLocationOfResidenceRequired,
  isPostalCodeRequired,
  isStateOfPropertyRequired,
  isVRERequired,
} from '../helpers';

// Personal Information
import CustomPageReviewField from '../../components/CustomPageReviewField';
import SchoolStateOrResidencyStateCustomPage from '../../containers/SchoolStateOrResidencyStatePage';
import { YourVAHealthFacilityPage } from '../../containers/YourVAHealthFacility';
import aboutTheFamilyMemberPage from '../chapters/personalInformation/aboutTheFamilyMember';
import aboutTheVeteranPage from '../chapters/personalInformation/aboutTheVeteran';
import aboutYourselfPage from '../chapters/personalInformation/aboutYourself';
import aboutYourselfGeneralPage from '../chapters/personalInformation/aboutYourselfGeneral';
import aboutYourselfRelationshipFamilyMemberPage from '../chapters/personalInformation/aboutYourselfRelationshipFamilyMember';
import addressValidationPage from '../chapters/personalInformation/addressValidation';
import deathDatePage from '../chapters/personalInformation/deathDate';
import familyMembersLocationOfResidencePage from '../chapters/personalInformation/familyMembersLocationOfResidence';
import familyMembersPostalCodePage from '../chapters/personalInformation/familyMembersPostalCode';
import isQuestionAboutVeteranOrSomeoneElsePage from '../chapters/personalInformation/isQuestionAboutVeteranOrSomeoneElse';
import moreAboutYourRelationshipToVeteranPage from '../chapters/personalInformation/moreAboutYourRelationshipToVeteran';
import aboutYourRelationshipToFamilyMemberPage from '../chapters/personalInformation/relationshipToFamilyMember';
import useFacilityInYourProfilePage, {
  radioLabels as useFacilityInProfileRadioLabels,
} from '../chapters/personalInformation/useFacilityInYourProfile';
import schoolStOrResidencyPage from '../chapters/personalInformation/schoolStOrResidency';
import searchSchoolsPage from '../chapters/personalInformation/searchSchools';
import stateOfFacilityPage from '../chapters/personalInformation/stateOfFacility';
import stateOfPropertyPage from '../chapters/personalInformation/stateOfProperty';
import stateOfSchoolPage from '../chapters/personalInformation/stateOfSchool';
import theirRelationshipToVeteranPage from '../chapters/personalInformation/theirRelationshipToVeteran';
import theirVRECounselorPage from '../chapters/personalInformation/theirVRECounselor';
import theirVREInformationPage from '../chapters/personalInformation/theirVREInformation';
import saveThisFacilityPage from '../chapters/personalInformation/saveThisFacility';
import veteransLocationOfResidencePage from '../chapters/personalInformation/veteransLocationOfResidence';
import veteransPostalCodePage from '../chapters/personalInformation/veteransPostalCode';
import yourBranchOfServicePage from '../chapters/personalInformation/yourBranchOfService';
import yourContactInformationPage from '../chapters/personalInformation/yourContactInformation';
import yourLocationOfResidencePage from '../chapters/personalInformation/yourLocationOfResidence';
import yourMailingAddressPage from '../chapters/personalInformation/yourMailingAddress';
import yourPostalCodePage from '../chapters/personalInformation/yourPostalCode';
import yourRolePage from '../chapters/personalInformation/yourRole';
import yourRoleEducationPage from '../chapters/personalInformation/yourRoleEducation';
import yourVRECounselorPage from '../chapters/personalInformation/yourVRECounselor';
import yourVREInformationPage from '../chapters/personalInformation/yourVREInformation';

export const flowPaths = {
  aboutMyselfRelationshipVeteran: 'about-myself-relationship-veteran',
  aboutMyselfRelationshipFamilyMember:
    'about-myself-relationship-family-member',
  aboutSomeoneElseRelationshipVeteran:
    'about-someone-else-relationship-veteran',
  aboutSomeoneElseRelationshipFamilyMember:
    'about-someone-else-relationship-family-member',
  aboutSomeoneElseRelationshipFamilyMemberAboutVeteran:
    'about-someone-else-relationship-family-member-about-veteran',
  aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMember:
    'about-someone-else-relationship-family-member-about-family-member',
  aboutSomeoneElseRelationshipVeteranOrFamilyMemberEducation:
    'about-someone-else-relationship-veteran-or-family-member-education',
  aboutSomeoneElseRelationshipConnectedThroughWork:
    'about-someone-else-relationship-connected-through-work',
  aboutSomeoneElseRelationshipConnectedThroughWorkEducation:
    'about-someone-else-relationship-connected-through-work-education',
  general: 'general-question',
};

export const ch3Pages = {
  yourRole: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.YOUR_ROLE.TITLE,
    uiSchema: yourRolePage.uiSchema,
    schema: yourRolePage.schema,
    CustomPageReview: CustomPageReviewField,
  },
  yourRoleEducation: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.YOUR_ROLE.TITLE,
    uiSchema: yourRoleEducationPage.uiSchema,
    schema: yourRoleEducationPage.schema,
    CustomPageReview: CustomPageReviewField,
  },
  moreAboutYourRelationshipToVeteran: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.MORE_ABOUT_YOUR_RELATIONSHIP_TO_VETERAN.TITLE,
    uiSchema: moreAboutYourRelationshipToVeteranPage.uiSchema,
    schema: moreAboutYourRelationshipToVeteranPage.schema,
    CustomPageReview: CustomPageReviewField,
    depends: form => form.relationshipToVeteran !== "I'm the Veteran",
  },
  aboutTheVeteran: {
    title: CHAPTER_3.ABOUT_THE_VET.TITLE,
    uiSchema: aboutTheVeteranPage.uiSchema,
    schema: aboutTheVeteranPage.schema,
    reviewTitle: "Veteran's personal information",
  },
  dateOfDeath: {
    title: CHAPTER_3.DEATH_DATE.TITLE,
    uiSchema: deathDatePage.uiSchema,
    schema: deathDatePage.schema,
    depends: form => form.aboutTheVeteran?.isVeteranDeceased === true,
  },
  veteransPostalCode: {
    title: CHAPTER_3.VETERANS_POSTAL_CODE.TITLE,
    uiSchema: veteransPostalCodePage.uiSchema,
    schema: veteransPostalCodePage.schema,
    depends: form => isPostalCodeRequired(form),
  },
  veteransLocationOfResidence: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.VETERAN_LOCATION_OF_RESIDENCE.TITLE,
    uiSchema: veteransLocationOfResidencePage.uiSchema,
    schema: veteransLocationOfResidencePage.schema,
    depends: form => isLocationOfResidenceRequired(form),
  },
  familyMembersPostalCode: {
    title: CHAPTER_3.FAMILY_MEMBERS_POSTAL_CODE.TITLE,
    uiSchema: familyMembersPostalCodePage.uiSchema,
    schema: familyMembersPostalCodePage.schema,
    depends: form => isPostalCodeRequired(form),
  },
  yourPostalCode: {
    title: CHAPTER_3.YOUR_POSTAL_CODE.TITLE,
    uiSchema: yourPostalCodePage.uiSchema,
    schema: yourPostalCodePage.schema,
    depends: form => isPostalCodeRequired(form),
  },
  isQuestionAboutVeteranOrSomeoneElse: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.WHO_QUES_IS_ABOUT.TITLE,
    uiSchema: isQuestionAboutVeteranOrSomeoneElsePage.uiSchema,
    schema: isQuestionAboutVeteranOrSomeoneElsePage.schema,
    CustomPageReview: CustomPageReviewField,
    onNavForward: ({ formData, goPath }) => {
      if (formData.isQuestionAboutVeteranOrSomeoneElse === 'Veteran') {
        goPath(
          `/${flowPaths.aboutSomeoneElseRelationshipFamilyMemberAboutVeteran}-1`,
        );
      } else {
        goPath(
          `/${flowPaths.aboutSomeoneElseRelationshipFamilyMemberAboutFamilyMember}-1`,
        );
      }
    },
  },
  aboutYourself: {
    title: CHAPTER_3.ABOUT_YOURSELF.TITLE,
    uiSchema: aboutYourselfPage.uiSchema,
    schema: aboutYourselfPage.schema,
    reviewTitle: 'Your personal information',
    depends: form => !form.hasPrefillInformation,
  },
  aboutYourselfGeneral: {
    title: CHAPTER_3.ABOUT_YOURSELF.TITLE,
    uiSchema: aboutYourselfGeneralPage.uiSchema,
    schema: aboutYourselfGeneralPage.schema,
    reviewTitle: 'Your personal information',
    depends: form => !form.hasPrefillInformation,
  },
  aboutYourselfRelationshipFamilyMember: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.ABOUT_YOURSELF.TITLE,
    uiSchema: aboutYourselfRelationshipFamilyMemberPage.uiSchema,
    schema: aboutYourselfRelationshipFamilyMemberPage.schema,
    reviewTitle: 'Your personal information',
    depends: form => !form.hasPrefillInformation,
  },
  facilityInYourProfile: {
    title: useFacilityInYourProfilePage.title,
    uiSchema: useFacilityInYourProfilePage.uiSchema,
    schema: useFacilityInYourProfilePage.schema,
    depends: form =>
      // Reference: https://va.ghe.com/software/va.gov-team/blob/master/products/ask-va/design/Fields,%20options%20and%20labels/Field%20rules.md#school-fields
      form.schoolInfo?.schoolName &&
      ((form.selectCategory === CategoryDebt &&
        form.selectTopic === TopicEducationBenefitOverpayments) ||
        form.yourRole === yourRoleOptionsEducation.SCO ||
        form.yourRole ===
          yourRoleOptionsEducation.TRAINING_OR_APPRENTICESHIP_SUP ||
        ((form.selectTopic === 'Federal programs & training facilities' ||
          form.selectTopic === 'Foreign programs & training facilities') &&
          form.relationshipToVeteran ===
            "I'm connected to the Veteran through my work (for example, as a School Certifying Official or fiduciary)")),
  },
  searchSchools: {
    title: searchSchoolsPage.title,
    uiSchema: searchSchoolsPage.uiSchema,
    schema: searchSchoolsPage.schema,
    depends: form =>
      (!form.schoolInfo?.schoolName &&
        form.selectCategory === CategoryDebt &&
        form.selectTopic === TopicEducationBenefitOverpayments) ||
      (form.useSchoolInProfile === useFacilityInProfileRadioLabels.N &&
        form.selectCategory === CategoryDebt &&
        form.selectTopic === TopicEducationBenefitOverpayments) ||
      ((form.useSchoolInProfile === useFacilityInProfileRadioLabels.N ||
        !form.schoolInfo?.schoolName) &&
        (form.yourRole === yourRoleOptionsEducation.SCO ||
          form.yourRole ===
            yourRoleOptionsEducation.TRAINING_OR_APPRENTICESHIP_SUP)) ||
      ((form.useSchoolInProfile === useFacilityInProfileRadioLabels.N ||
        !form.schoolInfo?.schoolName) &&
        (form.selectTopic === 'Federal programs & training facilities' ||
          form.selectTopic === 'Foreign programs & training facilities') &&
        form.relationshipToVeteran ===
          "I'm connected to the Veteran through my work (for example, as a School Certifying Official or fiduciary)"),
  },
  schoolStOrResidency: {
    title: CHAPTER_3.SCHOOL.TITLE,
    editModeOnReviewPage: false,
    CustomPage: SchoolStateOrResidencyStateCustomPage,
    CustomPageReview: CustomPageReviewField,
    uiSchema: schoolStOrResidencyPage.uiSchema,
    schema: schoolStOrResidencyPage.schema,
    depends: formData => {
      return (
        formData.selectTopic !== 'Federal programs & training facilities' &&
        formData.selectTopic !== 'Foreign programs & training facilities'
      );
    },
  },
  // This only applies for category topic = Education benefit overpayments (for students):
  schoolStOrResidencyForDebtEduStudents: {
    title: CHAPTER_3.SCHOOL.TITLE,
    editModeOnReviewPage: false,
    CustomPage: SchoolStateOrResidencyStateCustomPage,
    CustomPageReview: CustomPageReviewField,
    uiSchema: schoolStOrResidencyPage.uiSchema,
    schema: schoolStOrResidencyPage.schema,
    depends: form =>
      form.selectCategory === CategoryDebt &&
      form.selectTopic === TopicEducationBenefitOverpaymentsForStudents,
  },
  stateOfSchool: {
    title: stateOfSchoolPage.title,
    uiSchema: stateOfSchoolPage.uiSchema,
    schema: stateOfSchoolPage.schema,
    depends: formData =>
      formData.school === 'My facility is not listed' &&
      formData.selectTopic !== 'Federal programs & training facilities' &&
      formData.selectTopic !== 'Foreign programs & training facilities',
  },
  stateOfFacility: {
    title: stateOfFacilityPage.title,
    uiSchema: stateOfFacilityPage.uiSchema,
    schema: stateOfFacilityPage.schema,
    depends: form =>
      (form.yourRole === yourRoleOptionsEducation.VA_EMPLOYEE ||
        form.yourRole === yourRoleOptionsEducation.WORK_STUDY_SUP ||
        form.yourRole === yourRoleOptionsEducation.OTHER) &&
      form.selectTopic !== 'Federal programs & training facilities' &&
      form.selectTopic !== 'Foreign programs & training facilities',
  },
  saveThisFacility: {
    title: saveThisFacilityPage.title,
    uiSchema: saveThisFacilityPage.uiSchema,
    schema: saveThisFacilityPage.schema,
    depends: form =>
      form.school &&
      form.school !== 'My facility is not listed' &&
      form.useSchoolInProfile === useFacilityInProfileRadioLabels.N,
  },

  yourContactInformation: {
    title: CHAPTER_3.CONTACT_INFORMATION.TITLE,
    uiSchema: yourContactInformationPage.uiSchema,
    schema: yourContactInformationPage.schema,
  },
  yourMailingAddress: {
    title: CHAPTER_3.YOUR_MAILING_ADDRESS.TITLE,
    uiSchema: yourMailingAddressPage.uiSchema,
    schema: yourMailingAddressPage.schema,
    depends: form => form.contactPreference === 'USMail',
  },
  addressValidation: {
    title: CHAPTER_3.ADDRESS_CONFIRM.TITLE,
    uiSchema: addressValidationPage.uiSchema,
    schema: addressValidationPage.schema,
    depends: form => form.contactPreference === 'USMail',
    onNavForward: ({ goPath }) => goPath(CHAPTER_2.PAGE_3.PATH),
  },
  aboutYourFamilyMember: {
    title: CHAPTER_3.ABOUT_YOUR_FAM_MEM.TITLE,
    uiSchema: aboutTheFamilyMemberPage.uiSchema,
    schema: aboutTheFamilyMemberPage.schema,
    reviewTitle: "Family member's personal information",
  },
  aboutYourRelationshipToFamilyMember: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.RELATIONSHIP_TO_FAM_MEM.TITLE,
    uiSchema: aboutYourRelationshipToFamilyMemberPage.uiSchema,
    schema: aboutYourRelationshipToFamilyMemberPage.schema,
    CustomPageReview: CustomPageReviewField,
  },
  theirRelationshipToVeteran: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.RELATIONSHIP_TO_FAM_MEM.TITLE,
    uiSchema: theirRelationshipToVeteranPage.uiSchema,
    schema: theirRelationshipToVeteranPage.schema,
    CustomPageReview: CustomPageReviewField,
  },
  familyMembersLocationOfResidence: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.FAMILY_MEMBERS_LOCATION_OF_RESIDENCE.TITLE,
    uiSchema: familyMembersLocationOfResidencePage.uiSchema,
    schema: familyMembersLocationOfResidencePage.schema,
    depends: form => isLocationOfResidenceRequired(form),
  },
  yourLocationOfResidence: {
    editModeOnReviewPage: false,
    title: CHAPTER_3.YOUR_LOCATION_OF_RESIDENCE.TITLE,
    uiSchema: yourLocationOfResidencePage.uiSchema,
    schema: yourLocationOfResidencePage.schema,
    depends: form => isLocationOfResidenceRequired(form),
  },
  yourVAHealthFacility: {
    depends: form => isHealthFacilityRequired(form),
    path: CHAPTER_3.YOUR_VA_HEALTH_FACILITY.PATH,
    title: CHAPTER_3.YOUR_VA_HEALTH_FACILITY.TITLE,
    CustomPage: YourVAHealthFacilityPage,
    CustomPageReview: null,
    schema: {
      // This does still need to be here or it'll throw an error
      type: 'object',
      properties: {}, // The properties can be empty
    },
    uiSchema: {}, // UI schema is completely ignored
  },
  yourVREInformation: {
    title: CHAPTER_3.YOUR_VRE_INFORMATION.TITLE,
    uiSchema: yourVREInformationPage.uiSchema,
    schema: yourVREInformationPage.schema,
    depends: form => isVRERequired(form),
  },
  yourVRECounselor: {
    title: CHAPTER_3.YOUR_VRE_COUNSELOR.TITLE,
    uiSchema: yourVRECounselorPage.uiSchema,
    schema: yourVRECounselorPage.schema,
    depends: form => form.yourVREInformation === true,
  },
  theirVREInformation: {
    title: CHAPTER_3.THEIR_VRE_INFORMATION.TITLE,
    uiSchema: theirVREInformationPage.uiSchema,
    schema: theirVREInformationPage.schema,
    depends: form => isVRERequired(form),
  },
  theirVRECounselor: {
    title: CHAPTER_3.THEIR_VRE_COUNSELOR.TITLE,
    uiSchema: theirVRECounselorPage.uiSchema,
    schema: theirVRECounselorPage.schema,
    depends: form => form.theirVREInformation === true,
  },
  yourBranchOfService: {
    title: CHAPTER_3.BRANCH_OF_SERVICE.TITLE,
    uiSchema: yourBranchOfServicePage.uiSchema,
    schema: yourBranchOfServicePage.schema,
    // TODO - Custom component due to alert message for prefill https://www.figma.com/design/aQ6JsjD4pvMxSVPAZHllMX/AVA-Page-Library?node-id=3029-74800&t=DA4C4u7Wrv7TMhYs-1
    depends: form => isBranchOfServiceRequired(form),
  },
  stateOfProperty: {
    title: stateOfPropertyPage.title,
    uiSchema: stateOfPropertyPage.uiSchema,
    schema: stateOfPropertyPage.schema,
    depends: form => isStateOfPropertyRequired(form),
  },
};
